(() => {
  const elementName = 'gpt-gift-milestones';
  const controllerKey = '__gptGiftControllerV2';
  const signalTimeout = 15000;
  // Presentation state survives native section replacement, not a new page load.
  const choiceRailPositions = new Map();
  const choiceCategories = new Map();

  const asString = value => String(value ?? '');
  const nextMicrotask = callback => {
    if (typeof queueMicrotask === 'function') queueMicrotask(callback);
    else Promise.resolve().then(callback);
  };

  class GiftCartController {
    constructor() {
      this.views = new Set();
      this.operationQueue = Promise.resolve();
      this.operationCount = 0;
      this.selectionRevision = 0;
      this.desiredVariantId = '';
      this.pendingSignal = null;
      this.reconciliationQueued = false;
      this.lastCartData = null;
      this.desiredQuantities = null;
      this.quantityContext = '';
      this.quantityWorkerQueued = false;
      this.quantityReset = false;
      this.quantityResetRetryRequired = false;
      this.quantityResetRetrying = false;
      this.reselectionNotice = false;
      this.tierChoiceIntent = null;
      this.tierWorkerQueued = false;
      this.tierFailureSignature = '';
      this.tierRetryRefreshing = false;

      this.installCartSubscription();
    }

    register(view) {
      this.views.add(view);
      this.syncViews();
      nextMicrotask(() => {
        this.resolveStateSignal();
        this.requestReconciliation();
      });
    }

    unregister(view) {
      this.views.delete(view);
    }

    installCartSubscription() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event?.cartData) this.lastCartData = event.cartData;
        if (this.pendingSignal?.kind === 'cart-update') {
          nextMicrotask(() => this.resolveSignal(event?.cartData || null));
        }
        nextMicrotask(() => {
          this.resolveStateSignal();
          this.syncViews();
          this.requestReconciliation();
        });
      });
    }

    resolveStateSignal() {
      if (this.pendingSignal?.kind !== 'view-state') return;
      this.resolveSignal(this.currentState());
    }

    createSignal(kind, matches = null) {
      if (this.pendingSignal) throw new Error('Another cart operation is still awaiting confirmation.');

      let resolvePromise;
      let rejectPromise;
      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });
      const timeoutId = setTimeout(() => {
        if (!this.pendingSignal || this.pendingSignal.promise !== promise) return;
        this.pendingSignal = null;
        rejectPromise(new Error('The cart did not confirm the update. Please try again.'));
      }, signalTimeout);

      this.pendingSignal = {
        kind,
        matches,
        promise,
        resolve: value => {
          clearTimeout(timeoutId);
          resolvePromise(value);
        },
        reject: error => {
          clearTimeout(timeoutId);
          rejectPromise(error);
        }
      };
      return promise;
    }

    resolveSignal(data) {
      const signal = this.pendingSignal;
      if (!signal) return;
      if (typeof signal.matches === 'function' && !signal.matches(data)) return;
      this.pendingSignal = null;
      signal.resolve(data);
    }

    rejectSignal(error) {
      const signal = this.pendingSignal;
      if (!signal) return;
      this.pendingSignal = null;
      signal.reject(error);
    }

    enqueue(operation, options = {}) {
      const run = this.operationQueue.catch(() => {}).then(async () => {
        this.operationCount += 1;
        this.syncViews();
        try {
          return await operation();
        } finally {
          this.operationCount = Math.max(0, this.operationCount - 1);
          this.syncViews();
          if (options.reconcileAfter) nextMicrotask(() => this.requestReconciliation());
        }
      });
      this.operationQueue = run.catch(() => {});
      return run;
    }

    waitForState(matches) {
      const state = this.currentState();
      if (matches(state)) return Promise.resolve(state);
      return this.createSignal('view-state', matches);
    }

    requestSelection(variantId) {
      if (this.currentState().selectionMode === 'tiered') {
        this.requestTieredChoice(variantId);
        return;
      }
      if (this.currentState().selectionMode === 'multiple') {
        this.requestQuantityChange(variantId, 1);
        return;
      }
      const targetVariantId = asString(variantId);
      const state = this.currentState();
      const selectable = new Set((state.selectableVariantIds || []).map(asString));
      if (!targetVariantId || !selectable.has(targetVariantId)) return;
      if (!this.desiredVariantId && asString(state.selectedVariantId) === targetVariantId) return;

      const revision = ++this.selectionRevision;
      this.desiredVariantId = targetVariantId;
      this.clearErrors();
      this.syncViews();

      this.enqueue(() => this.performSelection(revision, targetVariantId)).catch(error => {
        if (revision !== this.selectionRevision) return;
        this.desiredVariantId = '';
        this.syncViews();
        this.showError(this.currentState().errorLabel || error?.message || 'We could not update your gift. Please try again.');
      });
    }

    async performSelection(revision, variantId) {
      await this.settleCart();
      if (revision !== this.selectionRevision) return;
      let state = this.currentState();
      this.assertSelectable(state, variantId);

      const existingGiftLines = state.giftLines || [];
      if (existingGiftLines.length === 1
        && asString(existingGiftLines[0].variantId) === variantId
        && Number(existingGiftLines[0].quantity) === 1
        && (!state.campaignId || Number(existingGiftLines[0].chargedCents) === 0)) {
        this.desiredVariantId = '';
        this.clearErrors();
        this.syncViews();
        return;
      }

      const removedGiftKeys = new Set();
      while ((state.giftLines || []).length) {
        if (revision !== this.selectionRevision) return;
        const targetLine = state.giftLines[0];
        const targetKey = asString(targetLine?.key);
        if (!targetKey || removedGiftKeys.has(targetKey)) {
          throw new Error('The existing gift could not be safely removed. Please try again.');
        }
        removedGiftKeys.add(targetKey);
        await this.removeGiftLine(targetLine);
        state = this.currentState();
        if (!Array.isArray(state.giftLines)) {
          throw new Error('The refreshed gift state is not ready. Please try again.');
        }
        if ((state.giftLines || []).some(item => asString(item.key) === targetKey)) {
          throw new Error('The existing gift could not be safely removed. Please try again.');
        }
      }

      if (revision !== this.selectionRevision) return;
      state = this.currentState();
      this.assertSelectable(state, variantId);
      await this.addGiftLine(variantId, state.activeTier);

      if (revision !== this.selectionRevision) return;
      state = this.currentState();
      const confirmed = (state.giftLines || []).filter(line => asString(line.variantId) === variantId);
      if (confirmed.length !== 1 || Number(confirmed[0].quantity) !== 1) {
        throw new Error('The selected gift could not be confirmed. Please try again.');
      }

      if (state.campaignId && Number(confirmed[0].chargedCents) !== 0) {
        await this.removeGiftLine(confirmed[0]);
        throw new Error(state.errorLabel);
      }
      this.desiredVariantId = '';
      this.clearErrors();
      this.syncViews();
    }

    assertSelectable(state, variantId) {
      const selectable = new Set((state.selectableVariantIds || []).map(asString));
      if (!state.campaignEnabled || Number(state.activeTier) <= 0 || !selectable.has(asString(variantId)) || (state.campaignId && !state.claimsEnabled)) {
        throw new Error('This gift is no longer available for the current cart.');
      }
    }

    async addGiftLine(variantId, tier, quantity = 1, cartSettled = false) {
      if (!cartSettled) await this.settleCart();
      this.assertSelectable(this.currentState(), variantId);
      const before = this.currentState();
      const multiple = before.selectionMode === 'multiple' || before.selectionMode === 'tiered';
      const beforeQuantity = this.giftQuantities(before).get(asString(variantId)) || 0;
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('The gift quantity is invalid.');
      if (multiple && this.totalGiftQuantity(before) + quantity > this.quantityAllowance(before)) {
        throw new Error('The gift allowance changed. Please check your selection.');
      }
      if (before.selectionMode === 'tiered') {
        const target = this.tierTarget(before);
        if (beforeQuantity + quantity > (target.get(asString(variantId)) || 0)) {
          throw new Error('The gift entitlement changed. Please check your selection.');
        }
      }
      const view = this.currentView();
      const form = view?.querySelector('[data-gpt-gift-product-form] form');
      const idInput = form?.querySelector('input[name="id"]');
      const tierInput = form?.querySelector('input[name="properties[_gpt_gift_tier]"]');
      const quantityInput = form?.querySelector('input[name="quantity"]');
      const submitButton = form?.querySelector('[type="submit"]');
      const errorWrapper = view?.querySelector('[data-gpt-gift-product-form] .product-form__error-message-wrapper');
      if (!form || !idInput || !tierInput || !submitButton) {
        throw new Error('The gift selector is not ready. Please try again.');
      }
      if (multiple && !quantityInput) throw new Error('The gift quantity selector is not ready.');

      idInput.value = asString(variantId);
      idInput.disabled = false;
      tierInput.value = asString(tier);
      if (quantityInput) quantityInput.value = asString(quantity);
      const isConfirmed = state => multiple
        ? (this.giftQuantities(state).get(asString(variantId)) || 0) >= beforeQuantity + quantity
        : (state.giftLines || []).some(line => asString(line.variantId) === asString(variantId) && Number(line.quantity) === 1);

      // Shrine publishes cartUpdate and then synchronously renders the Drawer.
      // Resolving on the next microtask therefore observes Shrine's completed UI
      // without depending on the exact shape of its cart/add response payload.
      const confirmation = this.createSignal('cart-update');

      let errorObserver = null;
      if (errorWrapper && typeof MutationObserver === 'function') {
        errorObserver = new MutationObserver(() => {
          if (errorWrapper.hidden) return;
          const message = errorWrapper.querySelector('.product-form__error-message')?.textContent?.trim();
          this.rejectSignal(new Error(message || 'We could not add this gift. Please try again.'));
        });
        errorObserver.observe(errorWrapper, { attributes: true, childList: true, subtree: true });
      }

      try {
        form.requestSubmit(submitButton);
        try {
          await confirmation;
          await this.waitForState(isConfirmed);
        } catch (error) {
          const confirmedLate = isConfirmed(this.currentState());
          if (!confirmedLate) throw error;
        }
      } finally {
        errorObserver?.disconnect();
      }
    }

    async removeGiftLine(line, cartSettled = false) {
      return this.changeGiftLineQuantity(line, 0, cartSettled);
    }

    async changeGiftLineQuantity(line, quantity, cartSettled = false) {
      if (!cartSettled) await this.settleCart();
      const targetKey = asString(line?.key);
      if (!targetKey) {
        throw new Error('The gift line could not be identified safely.');
      }

      const state = this.currentState();
      if (!Array.isArray(state.giftLines)) {
        throw new Error('The gift state is not ready for a safe removal.');
      }
      const currentLine = (state.giftLines || []).find(item => asString(item.key) === targetKey);
      if (!currentLine) return;
      if (asString(currentLine.variantId) !== asString(line.variantId)
        || Number(currentLine.quantity) !== Number(line.quantity)) return;
      if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity >= Number(currentLine.quantity)) {
        throw new Error('The gift quantity reduction is invalid.');
      }

      const lineIndex = Number(currentLine.index);
      if (!Number.isInteger(lineIndex) || lineIndex < 1) {
        throw new Error('The gift line position could not be confirmed safely.');
      }

      const cartElement = this.activeCartElement();
      if (!cartElement || typeof cartElement.updateQuantity !== 'function') {
        throw new Error('The cart is not ready to remove the expired gift.');
      }

      const lineKey = targetKey;
      const variantId = asString(currentLine.variantId);
      const expectedVariantQuantity = (this.giftQuantities(state).get(variantId) || 0) - Number(currentLine.quantity) + quantity;
      const isConfirmed = freshState => {
        if (!Array.isArray(freshState.giftLines)) return false;
        if (quantity === 0) return !freshState.giftLines.some(item => asString(item.key) === lineKey);
        return (this.giftQuantities(freshState).get(variantId) || 0) === expectedVariantQuantity;
      };
      const confirmation = this.createSignal('cart-update', data => {
        const freshState = this.currentState();
        if (!isConfirmed(freshState)) return false;
        if (!Array.isArray(data?.items)) return true;
        if (quantity === 0) return !data.items.some(item => asString(item.key) === lineKey);
        return true;
      });

      try {
        const nativeResult = cartElement.updateQuantity(lineIndex, quantity, undefined, {source:'sissilux-gift'});
        Promise.resolve(nativeResult).catch(error => this.rejectSignal(error));
        await confirmation;
        await this.waitForState(isConfirmed);
      } catch (error) {
        this.rejectSignal(error);
        throw error;
      }
    }

    async settleCart() {
      if (this.currentState().campaignId && window.SissiluxWarranty?.ensure) {
        await window.SissiluxWarranty.ensure();
      }
    }

    activeCartElement() {
      const pageCart = document.querySelector('cart-items');
      if (/\/cart\/?$/.test(window.location.pathname) && pageCart) return pageCart;
      return document.querySelector('cart-drawer-items') || pageCart;
    }

    requestReconciliation() {
      if (window.SissiluxCartTransactions?.held) return;
      const state = this.currentState();
      if (state.campaignId && !state.managementEnabled) return;
      if (state.selectionMode === 'tiered') {
        this.queueTierWork();
        return;
      }
      if (state.selectionMode === 'multiple') {
        this.detectQuantityReset(state);
        this.queueQuantityWork();
        return;
      }
      if (this.reconciliationQueued || this.desiredVariantId || this.operationCount > 0 || !this.currentView()) return;
      this.reconciliationQueued = true;
      nextMicrotask(() => {
        this.reconciliationQueued = false;
        if (this.desiredVariantId || this.operationCount > 0 || !this.currentView()) return;
        this.enqueue(() => this.reconcileGiftLines()).catch(error => {
          console.warn('Gift reconciliation will retry after the next cart update.', error);
        });
      });
    }

    async reconcileGiftLines() {
      if (this.currentState().selectionMode === 'tiered') return this.performTiered();
      if (this.currentState().selectionMode === 'multiple') {
        return this.performQuantities();
      }
      const removedGiftKeys = new Set();
      while (!this.desiredVariantId) {
        const state = this.currentState();
        const invalidLine = this.firstInvalidGiftLine(state);
        if (!invalidLine) return;
        const invalidKey = asString(invalidLine.key);
        if (!invalidKey || removedGiftKeys.has(invalidKey)) {
          throw new Error('Gift reconciliation stopped because the cart state did not advance.');
        }
        removedGiftKeys.add(invalidKey);
        await this.removeGiftLine(invalidLine);
        const freshState = this.currentState();
        if (!Array.isArray(freshState.giftLines)) {
          throw new Error('Gift reconciliation could not read the refreshed cart state.');
        }
        if ((freshState.giftLines || []).some(item => asString(item.key) === invalidKey)) {
          throw new Error('Gift reconciliation could not confirm the expired gift removal.');
        }
      }
    }

    firstInvalidGiftLine(state) {
      if (state.selectionMode === 'tiered') return this.quantityCorrection(state, this.tierTarget(state))?.line || null;
      if (state.selectionMode === 'multiple') {
        if (this.resetting || this.totalGiftQuantity(state) > this.quantityAllowance(state)) return state.giftLines?.[0] || null;
        return this.quantityCorrection(state, this.normaliseQuantities(state, this.giftQuantities(state, true)))?.line || null;
      }
      const selectable = new Set((state.selectableVariantIds || []).map(asString));
      const campaignActive = Boolean(state.campaignEnabled) && Number(state.activeTier) > 0;
      let keptOne = false;

      for (const line of state.giftLines || []) {
        const allowed = campaignActive
          && selectable.has(asString(line.variantId))
          && Number(line.quantity) === 1
          && (!state.campaignId || Number(line.chargedCents) === 0)
          && !keptOne;
        if (!allowed) return line;
        keptOne = true;
      }
      return null;
    }

    get hasPendingChanges() {
      return Boolean(this.operationCount || this.desiredVariantId || this.desiredQuantities || this.quantityWorkerQueued || this.reconciliationQueued || this.resetting || this.tierChoiceIntent || this.tierWorkerQueued);
    }

    get resetting() {
      return this.quantityReset;
    }

    get safeForCheckout() {
      if (this.hasPendingChanges) return false;
      const state = this.currentState();
      if (state.selectionMode === 'tiered') {
        return Array.isArray(state.giftLines)
          && (!state.unifiedChoices || this.totalGiftQuantity(state) <= this.quantityAllowance(state))
          && !this.quantityCorrection(state, this.tierTarget(state));
      }
      if (state.selectionMode !== 'multiple') return true;
      if (!Array.isArray(state.giftLines)) return false;
      if (this.totalGiftQuantity(state) > this.quantityAllowance(state)) return false;
      const allowed = new Set((state.selectableVariantIds || []).map(asString));
      return (state.giftLines || []).every(line => allowed.has(asString(line.variantId))
        && Number.isSafeInteger(Number(line.quantity)) && Number(line.quantity) > 0
        && (!state.campaignId || Number(line.chargedCents) === 0));
    }

    get settled() {
      return this.safeForCheckout;
    }

    detectQuantityReset(state) {
      if (!state.selectionMode) return;
      if (state.selectionMode !== 'multiple') {
        this.quantityReset = false;
        this.quantityResetRetryRequired = false;
        this.reselectionNotice = false;
        return;
      }
      if (!state.managementEnabled || this.totalGiftQuantity(state) <= this.quantityAllowance(state)) return;
      this.quantityReset = true;
      this.reselectionNotice = true;
      this.desiredQuantities = null;
      this.quantityContext = '';
    }

    retryQuantityReset() {
      if (!this.resetting || !this.quantityResetRetryRequired || this.quantityWorkerQueued) return this.operationQueue;
      this.quantityResetRetryRequired = false;
      this.quantityResetRetrying = true;
      this.clearErrors();
      this.queueQuantityWork();
      return this.operationQueue;
    }

    quantityAllowance(state) {
      const quantity = Number(state.giftAllowance);
      if (!state.campaignEnabled || !state.claimsEnabled || Number(state.activeTier) <= 0) return 0;
      return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 0;
    }

    giftQuantities(state, onlyFree = false) {
      const quantities = new Map();
      for (const line of state.giftLines || []) {
        const quantity = Number(line.quantity);
        const variantId = asString(line.variantId);
        if (!variantId || !Number.isSafeInteger(quantity) || quantity < 1) continue;
        if (onlyFree && state.campaignId && Number(line.chargedCents) !== 0) continue;
        quantities.set(variantId, (quantities.get(variantId) || 0) + quantity);
      }
      return quantities;
    }

    totalGiftQuantity(state) {
      return [...this.giftQuantities(state).values()].reduce((sum, quantity) => sum + quantity, 0);
    }

    currentQuantityContext(state) {
      return [state.campaignId, state.country, state.selectionMode].map(asString).join('|');
    }

    tierContext(state) {
      return [state.campaignId, state.country, state.selectionMode, state.tierKey].map(asString).join('|');
    }

    tierActive(state) {
      return Boolean(state.campaignEnabled && state.claimsEnabled && Number(state.activeTier) > 0);
    }

    tierFixedQuantities(state) {
      const result = new Map();
      if (!this.tierActive(state)) return result;
      const allowed = new Set((state.selectableVariantIds || []).map(asString));
      for (const gift of state.fixedGifts || []) {
        const id = asString(gift.variantId), quantity = Number(gift.quantity);
        if (gift.available === true && allowed.has(id) && Number.isSafeInteger(quantity) && quantity > 0) result.set(id, quantity);
      }
      return result;
    }

    tierGroups(state) {
      if (!this.tierActive(state)) return [];
      const fixed = new Set((state.fixedGifts || []).map(gift => asString(gift.variantId)));
      const allowed = new Set((state.selectableVariantIds || []).map(asString));
      const seen = new Set();
      const groups = Array.isArray(state.choiceGroups) ? state.choiceGroups
        : [{id:'premium', limit:state.choiceLimit, variantIds:state.choiceVariantIds}];
      return groups.filter(group => Number(group.limit) === 1 || (state.unifiedChoices && Number(group.limit) > 0)).map(group => ({
        id:asString(group.id),
        variantIds:(group.variantIds || []).map(asString).filter(id => {
          if (!allowed.has(id) || fixed.has(id) || seen.has(id)) return false;
          seen.add(id);
          return true;
        })
      }));
    }

    tierChoices(state, groupId) {
      return new Set(this.tierGroups(state).filter(group => groupId === undefined || group.id === groupId).flatMap(group => group.variantIds));
    }

    tierSelectedChoice(state, useIntent = true, groupId = 'premium') {
      const choices = this.tierChoices(state, groupId);
      const intended = this.tierChoiceIntent?.selections?.[groupId];
      if (useIntent && this.tierChoiceIntent?.context === this.tierContext(state) && choices.has(intended)) return intended;
      const line = (state.giftLines || []).find(gift => choices.has(asString(gift.variantId)) && Number(gift.quantity) > 0 && Number(gift.chargedCents) === 0);
      return line ? asString(line.variantId) : '';
    }

    tierSelections(state, useIntent = true) {
      if (state.unifiedChoices) {
        const allowed = this.tierChoices(state);
        const intended = useIntent && this.tierChoiceIntent?.context === this.tierContext(state)
          ? this.tierChoiceIntent.selections : null;
        const ids = intended ? Object.values(intended) : (state.giftLines || [])
          .filter(line => Number(line.quantity) > 0 && Number(line.chargedCents) === 0).map(line => asString(line.variantId));
        if (new Set(ids).size > this.quantityAllowance(state)) {
          // Latch an empty target across successive cart refreshes: removing only
          // the first excess line must not silently keep the other old gift.
          if (useIntent) {
            this.tierChoiceIntent = {context:this.tierContext(state), selections:{}};
            this.unifiedReselectionContext = this.tierContext(state);
          }
          return {};
        }
        // Separate entitlements: a filled premium slot never consumes the standard slot.
        let valid = [...new Set(ids)].filter(id => allowed.has(id));
        if (state.splitChoices) {
          valid = this.tierGroups(state).flatMap(group => {
            const selected = valid.filter(id => group.variantIds.includes(id));
            // An old invalid pair needs a new choice; never choose a favorite for the customer.
            return selected.length <= 1 ? selected : [];
          });
        }
        return Object.fromEntries(valid.map(id => [id,id]));
      }
      return Object.fromEntries(this.tierGroups(state).map(group => [group.id, this.tierSelectedChoice(state, useIntent, group.id)]));
    }

    tierTarget(state, useIntent = true) {
      const target = this.tierFixedQuantities(state);
      for (const choice of Object.values(this.tierSelections(state, useIntent))) if (choice) target.set(choice, 1);
      return target;
    }

    tierWorkSignature(state) {
      return JSON.stringify([this.tierContext(state), state.campaignEnabled, state.claimsEnabled,
        state.fixedGifts, state.choiceGroups, state.choiceVariantIds, state.choiceLimit, state.selectableVariantIds, state.giftLines]);
    }

    tierRetryRequired(state = this.currentState()) {
      return Boolean(this.tierFailureSignature && this.tierFailureSignature === this.tierWorkSignature(state));
    }

    requestTieredChoice(variantId) {
      if (window.SissiluxCartTransactions?.held) return;
      const state = this.currentState(), id = asString(variantId);
      if (state.selectionMode !== 'tiered' || !state.managementEnabled || !this.tierChoices(state).has(id)) return;
      if (this.tierFailureSignature) this.tierRetryRefreshing = true;
      this.tierFailureSignature = '';
      const context = this.tierContext(state);
      if (state.unifiedChoices) {
        const selections = {...this.tierSelections(state)}, limit = this.quantityAllowance(state);
        if (state.splitChoices) {
          const group = this.tierGroups(state).find(group => group.variantIds.includes(id));
          for (const previous of group.variantIds) delete selections[previous];
          selections[id] = id;
          this.tierChoiceIntent = {context,selections};
        } else if (limit === 1) {
          this.tierChoiceIntent = {context, selections:{[id]:id}};
        } else {
          if (selections[id]) delete selections[id];
          else if (Object.keys(selections).length < limit) selections[id] = id;
          else return;
          this.tierChoiceIntent = {context,selections};
        }
        this.clearErrors(); this.syncViews(); this.queueTierWork();
        return;
      }
      const group = this.tierGroups(state).find(group => group.variantIds.includes(id));
      const selections = this.tierChoiceIntent?.context === context ? {...this.tierChoiceIntent.selections} : {};
      selections[group.id] = id;
      this.tierChoiceIntent = {context, selections};
      this.clearErrors();
      this.syncViews();
      this.queueTierWork();
    }

    retryTiered() {
      if (window.SissiluxCartTransactions?.held) return this.operationQueue;
      if (this.currentState().selectionMode !== 'tiered' || this.tierWorkerQueued) return this.operationQueue;
      this.tierFailureSignature = '';
      this.tierRetryRefreshing = true;
      this.clearErrors();
      this.queueTierWork();
      return this.operationQueue;
    }

    queueTierWork() {
      if (window.SissiluxCartTransactions?.held) return;
      const state = this.currentState();
      if (state.selectionMode !== 'tiered' || !state.managementEnabled || !this.currentView() || this.tierWorkerQueued) return;
      if (this.tierChoiceIntent?.context !== this.tierContext(state)) this.tierChoiceIntent = null;
      if (this.tierFailureSignature && !this.tierRetryRequired(state)) this.tierFailureSignature = '';
      if (this.tierRetryRequired(state)) return;
      if (!this.tierRetryRefreshing && !this.quantityCorrection(state, this.tierTarget(state))) {
        this.tierChoiceIntent = null;
        this.clearErrors();
        this.syncViews();
        return;
      }
      this.tierWorkerQueued = true;
      this.enqueue(() => this.performTiered()).catch(error => {
        this.tierChoiceIntent = null;
        this.tierRetryRefreshing = false;
        this.tierFailureSignature = this.tierWorkSignature(this.currentState());
        this.showError(this.currentState().errorLabel || error?.message || 'We could not update your gifts. Please try again.');
      }).finally(() => {
        this.tierWorkerQueued = false;
        this.syncViews();
        nextMicrotask(() => this.requestReconciliation());
      });
    }

    async performTiered() {
      if (this.tierRetryRefreshing) {
        if (!window.SissiluxWarranty?.ensure) throw new Error('Refresh the cart before retrying your gifts.');
        await window.SissiluxWarranty.ensure(undefined, true, true);
        this.tierRetryRefreshing = false;
      }
      const seen = new Set();
      while (this.currentView()) {
        let state = this.currentState();
        if (state.selectionMode !== 'tiered' || !state.managementEnabled) {
          this.tierChoiceIntent = null;
          return;
        }
        if (this.tierChoiceIntent?.context !== this.tierContext(state)) this.tierChoiceIntent = null;
        if (!Array.isArray(state.giftLines)) throw new Error('The refreshed gift state is not ready.');
        if (!this.quantityCorrection(state, this.tierTarget(state))) {
          this.tierChoiceIntent = null;
          this.tierFailureSignature = '';
          this.clearErrors();
          return;
        }
        await this.settleCart();
        state = this.currentState();
        if (state.selectionMode !== 'tiered' || !state.managementEnabled) continue;
        if (this.tierChoiceIntent?.context !== this.tierContext(state)) this.tierChoiceIntent = null;
        if (!Array.isArray(state.giftLines)) throw new Error('The refreshed gift state is not ready.');
        const target = this.tierTarget(state), correction = this.quantityCorrection(state, target);
        if (!correction) continue;
        const signature = JSON.stringify([this.tierContext(state), state.giftLines, [...target], correction]);
        if (seen.has(signature)) throw new Error('The gift update could not be confirmed. Please try again.');
        seen.add(signature);
        if (correction.line) await this.changeGiftLineQuantity(correction.line, correction.quantity, true);
        else await this.addGiftLine(correction.variantId, state.activeTier, correction.quantity, true);
      }
      this.tierChoiceIntent = null;
    }

    normaliseQuantities(state, requested) {
      const allowed = new Set((state.selectableVariantIds || []).map(asString));
      let remaining = this.quantityAllowance(state);
      const result = new Map();
      for (const [variantId, value] of requested) {
        const quantity = Number(value);
        if (!allowed.has(variantId) || !Number.isSafeInteger(quantity) || quantity < 1 || remaining < 1) continue;
        const keep = Math.min(quantity, remaining);
        result.set(variantId, keep);
        remaining -= keep;
      }
      return result;
    }

    requestQuantityChange(variantId, delta) {
      const state = this.currentState();
      if (state.selectionMode !== 'multiple' || !state.managementEnabled || (delta !== 1 && delta !== -1)) return;
      this.detectQuantityReset(state);
      if (this.resetting) {
        this.queueQuantityWork();
        this.syncViews();
        return;
      }
      const id = asString(variantId);
      const context = this.currentQuantityContext(state);
      const base = this.desiredQuantities && this.quantityContext === context ? this.desiredQuantities : this.giftQuantities(state, true);
      const target = this.normaliseQuantities(state, base);
      const quantity = target.get(id) || 0;
      if (delta === 1) {
        if (!(state.selectableVariantIds || []).map(asString).includes(id)) return;
        const total = [...target.values()].reduce((sum, value) => sum + value, 0);
        if (total >= this.quantityAllowance(state)) return;
        this.reselectionNotice = false;
        target.set(id, quantity + 1);
      } else {
        if (quantity < 1) return;
        if (quantity === 1) target.delete(id);
        else target.set(id, quantity - 1);
      }
      this.desiredQuantities = target;
      this.quantityContext = context;
      this.clearErrors();
      this.syncViews();
      this.queueQuantityWork();
    }

    queueQuantityWork() {
      if (this.quantityWorkerQueued || !this.currentView()) return;
      const state = this.currentState();
      if (state.selectionMode !== 'multiple' || !state.managementEnabled) return;
      if (this.quantityResetRetryRequired) return;
      if (!this.hasQuantityWork(state)) return;
      this.quantityWorkerQueued = true;
      this.enqueue(async () => {
        try {
          if (this.quantityResetRetrying) {
            if (!window.SissiluxWarranty?.ensure) throw new Error('Refresh the cart before retrying the gift reset.');
            await window.SissiluxWarranty.ensure(undefined, true, true);
            this.quantityResetRetrying = false;
          }
          await this.performQuantities();
        } catch (error) {
          this.desiredQuantities = null;
          this.quantityContext = '';
          if (this.resetting) {
            this.quantityResetRetryRequired = true;
            this.quantityResetRetrying = false;
          } else {
            try {
              await this.performQuantities();
            } catch (_) {}
          }
          this.showError(this.currentState().errorLabel || error?.message || 'We could not update your gifts. Please try again.');
        } finally {
          this.quantityWorkerQueued = false;
          this.syncViews();
        }
      }).then(() => {
        if (this.currentState().selectionMode !== 'multiple') this.requestReconciliation();
      });
    }

    quantityCorrection(state, target) {
      const allowed = new Set((state.selectableVariantIds || []).map(asString));
      const lines = state.giftLines || [];
      for (const line of lines) {
        if (!allowed.has(asString(line.variantId)) || !Number.isSafeInteger(Number(line.quantity)) || Number(line.quantity) < 1 || (state.campaignId && Number(line.chargedCents) !== 0)) {
          return {line, quantity:0};
        }
      }
      const actual = this.giftQuantities(state);
      for (const line of [...lines].reverse()) {
        const variantId = asString(line.variantId);
        const excess = (actual.get(variantId) || 0) - (target.get(variantId) || 0);
        if (excess > 0) return {line, quantity:Math.max(0, Number(line.quantity) - excess)};
      }
      for (const [variantId, quantity] of target) {
        const missing = quantity - (actual.get(variantId) || 0);
        if (missing > 0) return {variantId, quantity:missing};
      }
      return null;
    }

    hasQuantityWork(state) {
      // A latched reset must finish clearing every gift, even after the count
      // falls within the allowance. An unchanged valid cart needs no new read.
      return Boolean(this.resetting || this.desiredQuantities || this.firstInvalidGiftLine(state));
    }

    async performQuantities() {
      const seen = new Set();
      while (this.currentView()) {
        const observed = this.currentState();
        this.detectQuantityReset(observed);
        if (this.quantityResetRetryRequired) return;
        // A confirmed native render already contains the completed selection.
        // Finish locally instead of reading the cart again just to exit.
        if (observed.selectionMode === 'multiple' && observed.managementEnabled && Array.isArray(observed.giftLines)) {
          if (this.resetting && observed.giftLines.length === 0) {
            this.quantityReset = false;
            this.desiredQuantities = null;
            this.quantityContext = '';
            this.clearErrors();
            return;
          }
          if (!this.resetting) {
            if (this.desiredQuantities && this.quantityContext !== this.currentQuantityContext(observed)) {
              this.desiredQuantities = null;
              this.quantityContext = '';
            }
            const observedTarget = this.normaliseQuantities(observed, this.desiredQuantities || this.giftQuantities(observed, true));
            if (!this.quantityCorrection(observed, observedTarget)) {
              this.desiredQuantities = null;
              this.quantityContext = '';
              return;
            }
          }
        }
        if (!this.hasQuantityWork(observed)) return;
        await this.settleCart();
        let state = this.currentState();
        this.detectQuantityReset(state);
        if (state.selectionMode !== 'multiple' || !state.managementEnabled) {
          this.desiredQuantities = null;
          this.quantityContext = '';
          return;
        }
        if (!Array.isArray(state.giftLines)) throw new Error('The refreshed gift state is not ready. Please try again.');
        if (this.quantityResetRetryRequired) return;
        if (this.quantityReset) {
          this.desiredQuantities = null;
          this.quantityContext = '';
          const line = state.giftLines?.[0];
          if (!line) {
            this.quantityReset = false;
            this.quantityResetRetryRequired = false;
            this.clearErrors();
            return;
          }
          const resetSignature = JSON.stringify(['reset', state.giftLines, state.country]);
          if (seen.has(resetSignature)) throw new Error('The gift reset could not be confirmed. Please try again.');
          seen.add(resetSignature);
          await this.removeGiftLine(line, true);
          continue;
        }
        if (this.desiredQuantities && this.quantityContext !== this.currentQuantityContext(state)) {
          this.desiredQuantities = null;
          this.quantityContext = '';
        }
        const target = this.normaliseQuantities(state, this.desiredQuantities || this.giftQuantities(state, true));
        if (this.desiredQuantities) this.desiredQuantities = target;
        const correction = this.quantityCorrection(state, target);
        if (!correction) {
          this.desiredQuantities = null;
          this.quantityContext = '';
          return;
        }
        const signature = JSON.stringify([state.giftLines, [...target], correction, state.country, state.giftAllowance]);
        if (seen.has(signature)) throw new Error('The gift update could not be confirmed. Please try again.');
        seen.add(signature);
        // The current iteration already synchronized warranty and native writes.
        // No asynchronous work separates this fresh correction from submission.
        if (correction.line) await this.changeGiftLineQuantity(correction.line, correction.quantity, true);
        else await this.addGiftLine(correction.variantId, state.activeTier, correction.quantity, true);
        state = this.currentState();
        const chargedLines = (state.giftLines || []).filter(line => state.campaignId && Number(line.chargedCents) !== 0);
        if (chargedLines.length) {
          for (const line of chargedLines) await this.removeGiftLine(line);
          throw new Error(state.errorLabel || 'The gifts could not be confirmed free.');
        }
      }
      this.desiredQuantities = null;
      this.quantityContext = '';
    }

    currentView() {
      for (const view of this.views) {
        if (view.isConnected) return view;
      }
      return null;
    }

    currentState() {
      return this.currentView()?.readState() || {};
    }

    summaryPresentation(state, quantities, pending) {
      const summary = {...state, selectionPending:Boolean(pending)};
      if (state.selectionMode === 'tiered') {
        const selectedChoices = Object.values(this.tierSelections(state, pending)).filter(Boolean);
        const fixed = this.tierFixedQuantities(state);
        const confirmed = this.giftQuantities(state, true);
        summary.choiceSelectedQuantity = selectedChoices.length;
        summary.choiceSelectedValueCents = 0;
        for (const id of selectedChoices) {
          const amount = state.giftValues?.[id];
          if (amount === null || amount === undefined || amount === '' || !Number.isSafeInteger(Number(amount)) || Number(amount) < 0) summary.choiceSelectedValueCents = null;
          else if (summary.choiceSelectedValueCents !== null) summary.choiceSelectedValueCents += Number(amount);
        }
        summary.selectedGiftQuantity = [...quantities.values()].reduce((sum, quantity) => sum + quantity, 0);
        summary.selectedGiftValueCents = 0;
        summary.confirmedFixedGiftQuantity = 0;
        summary.fixedSatisfied = this.tierActive(state) && (state.fixedGifts || []).every(gift => {
          const id = asString(gift.variantId), quantity = Number(gift.quantity);
          return gift.available === true && fixed.has(id) && (confirmed.get(id) || 0) === quantity;
        });
        for (const [id, quantity] of quantities) {
          const amount = state.giftValues?.[id];
          const value = amount === null || amount === undefined || amount === '' ? NaN : Number(amount);
          if (!Number.isSafeInteger(value) || value < 0) {
            summary.selectedGiftValueCents = null;
          } else {
            if (summary.selectedGiftValueCents !== null) summary.selectedGiftValueCents += value * quantity;
          }
        }
        for (const [id, quantity] of fixed) summary.confirmedFixedGiftQuantity += Math.min(quantity, confirmed.get(id) || 0);
        return summary;
      }
      if (state.selectionMode !== 'multiple' || (!pending && !this.resetting)) return summary;
      const selected = this.resetting ? new Map() : quantities;
      let value = 0;
      let valuesKnown = true;
      for (const [id, quantity] of selected) {
        const amount = state.giftValues?.[id];
        if (amount === null || amount === undefined || amount === '' || !Number.isSafeInteger(Number(amount)) || Number(amount) < 0) {
          valuesKnown = false;
          continue;
        }
        value += Number(amount) * quantity;
      }
      summary.selectedGiftQuantity = [...selected.values()].reduce((sum, quantity) => sum + quantity, 0);
      summary.selectedGiftValueCents = valuesKnown && Number.isSafeInteger(value) ? value : null;
      return summary;
    }

    syncViews() {
      this.detectQuantityReset(this.currentState());
      const pendingVariantId = this.desiredVariantId;
      this.views.forEach(view => {
        if (!view.isConnected) return;
        const state = view.readState();
        let presentationState = state;
        if (state.selectionMode === 'tiered') {
          const pending = Boolean(this.tierChoiceIntent?.context === this.tierContext(state) || this.tierWorkerQueued);
          const quantities = pending ? this.tierTarget(state) : this.giftQuantities(state, true);
          view.applyTiered(this.tierSelections(state), {pending, retryRequired:this.tierRetryRequired(state)});
          presentationState = this.summaryPresentation(state, quantities, pending);
        } else if (state.selectionMode === 'multiple') {
          const pending = this.desiredQuantities && this.quantityContext === this.currentQuantityContext(state);
          const quantities = pending ? this.normaliseQuantities(state, this.desiredQuantities) : this.giftQuantities(state);
          view.applyQuantities(Object.fromEntries(quantities), {allowance:this.quantityAllowance(state), pending:Boolean(pending), resetting:this.resetting, reselectionNotice:this.reselectionNotice});
          presentationState = this.summaryPresentation(state, quantities, pending);
        } else {
          const selectedVariantId = pendingVariantId || asString(state.selectedVariantId);
          view.applySelection(selectedVariantId, Boolean(pendingVariantId));
        }
        view.setBusy(this.operationCount > 0 || Boolean(window.SissiluxCartTransactions?.held));
        // Display-only projection. Keep server JSON, checkout and analytics on
        // confirmed state; matching the source text prevents reuse after render.
        view.giftSummaryPresentation = {
          sourceText:view.querySelector('[data-gpt-gift-state]')?.textContent ?? null,
          state:presentationState
        };
        if (state.campaignId) view.dispatchEvent(new CustomEvent('sissilux:bfcm-state', {bubbles:true, detail:{presentation:view.giftSummaryPresentation}}));
      });
    }

    clearErrors() {
      this.views.forEach(view => view.showError(''));
    }

    showError(message) {
      this.views.forEach(view => view.showError(message));
    }
  }

  if (!window[controllerKey]) window[controllerKey] = new GiftCartController();
  const controller = window[controllerKey];

  if (customElements.get(elementName)) return;

  class GptGiftMilestones extends HTMLElement {
    connectedCallback() {
      if (!this.hasBoundEvents) {
        this.hasBoundEvents = true;
        this.addEventListener('click', event => this.handleClick(event));
        this.addEventListener('keydown', event => this.handleKeydown(event));
      }
      this.syncChoiceCategory();
      this.connectChoiceRail();
      controller.register(this);
    }

    disconnectedCallback() {
      this.disconnectChoiceRail();
      controller.unregister(this);
    }

    syncChoiceCategory(requested) {
      const state = this.readState();
      const key = JSON.stringify([this.closest?.('cart-drawer') ? 'drawer' : 'page',
        this.dataset?.blockId, state.campaignId, state.country, state.currency, state.cartToken]);
      const slots = [...this.querySelectorAll('[data-gm-gift-slot]')];
      const split = state.splitChoices && slots.length > 0;
      const category = split ? (requested || choiceCategories.get(key) || 'standard') : '';
      const active = slots.some(slot => slot.dataset.gmGiftSlot === category) ? category : (split ? 'standard' : '');
      if (split) {
        choiceCategories.delete(key);
        choiceCategories.set(key, active);
        if (choiceCategories.size > 24) choiceCategories.delete(choiceCategories.keys().next().value);
      } else choiceCategories.delete(key);
      const changed = this.activeChoiceCategory !== active;
      if (changed) {
        for (const record of this.choiceRails || []) record.remember();
        this.disconnectChoiceRail();
      }
      this.activeChoiceCategory = active;
      slots.forEach(slot => {
        const current = slot.dataset.gmGiftSlot === active;
        slot.classList.toggle('is-active', current);
        slot.setAttribute('aria-pressed', String(current));
      });
      this.querySelectorAll('[data-gm-choice-tier]').forEach(card => {
        card.hidden = Boolean(split && card.dataset.gmChoiceTier !== active);
      });
      if (changed && this.choiceRailConnected) {
        this.querySelectorAll('.siss-promo-rewards__grid').forEach(rail => { rail.scrollLeft = 0; });
        this.connectChoiceRail();
      }
    }

    connectChoiceRail() {
      this.choiceRailConnected = true;
      this.disconnectChoiceRail();
      const state = this.readState();
      if (state.selectionMode !== 'tiered') return;
      this.choiceRails = [...this.querySelectorAll('.siss-promo-rewards__grid')].map(rail => {
        const variants = [...rail.querySelectorAll('[data-gm-gift-card]')].filter(card => !card.closest('[data-gm-choice-tier]')?.hidden).map(card => card.dataset.variantId);
        const key = JSON.stringify([this.closest?.('cart-drawer') ? 'drawer' : 'page',
          this.dataset?.blockId, state.campaignId, state.country, state.currency, state.cartToken, this.activeChoiceCategory, variants]);
        const saved = choiceRailPositions.get(key);
        let restorePending = Number.isFinite(saved);
        const record = {rail, observer:null};
        const remember = () => {
          if (!this.isConnected || rail.clientWidth <= 0) return;
          restorePending = false;
          choiceRailPositions.delete(key);
          choiceRailPositions.set(key, Math.max(0, Math.min(rail.scrollWidth - rail.clientWidth, rail.scrollLeft)));
          if (choiceRailPositions.size > 24) choiceRailPositions.delete(choiceRailPositions.keys().next().value);
        };
        const restore = () => {
          if (!this.isConnected || !restorePending || rail.clientWidth <= 0 || !this.choiceRails?.includes(record)) return;
          rail.scrollLeft = Math.max(0, Math.min(rail.scrollWidth - rail.clientWidth, saved));
          remember();
          record.observer?.disconnect();
        };
        record.remember = remember;
        rail.addEventListener('scroll', remember, {passive:true});
        if (restorePending) {
          if (typeof ResizeObserver === 'function') {
            record.observer = new ResizeObserver(restore);
            record.observer.observe(rail);
          }
          if (typeof requestAnimationFrame === 'function') requestAnimationFrame(restore);
          else nextMicrotask(restore);
        }
        return record;
      });
    }

    disconnectChoiceRail() {
      for (const record of this.choiceRails || []) {
        record.rail.removeEventListener('scroll', record.remember);
        record.observer?.disconnect();
      }
      this.choiceRails = [];
    }

    readState() {
      try {
        return JSON.parse(this.querySelector('[data-gpt-gift-state]')?.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    handleClick(event) {
      const slot = event.target.closest('[data-gm-gift-slot]');
      if (slot) {
        event.preventDefault();
        this.syncChoiceCategory(slot.dataset.gmGiftSlot);
        return;
      }
      const change = event.target.closest('[data-gm-change-choice]');
      if (change) {
        event.preventDefault();
        const group = change.closest('[data-gm-choice-group]');
        const expanded = group.dataset.expanded !== 'true';
        group.dataset.expanded = String(expanded);
        group.querySelector('[data-gm-choice-options]').hidden = !expanded;
        change.setAttribute('aria-expanded', String(expanded));
        return;
      }
      if (event.target.closest('[data-gm-tier-retry]')) {
        event.preventDefault();
        controller.retryTiered();
        return;
      }
      const resetRetry = event.target.closest('[data-gm-gift-reset-retry]');
      if (resetRetry) {
        event.preventDefault();
        controller.retryQuantityReset();
        return;
      }
      const adjust = event.target.closest('[data-gm-gift-adjust]');
      if (adjust && this.readState().selectionMode === 'multiple') {
        if (!adjust.disabled) controller.requestQuantityChange(adjust.dataset.variantId, Number(adjust.dataset.gmGiftAdjust));
        return;
      }
      const previewOpen = event.target.closest('[data-gm-preview-open]');
      if (previewOpen) {
        this.openPreview(Number(previewOpen.dataset.previewIndex || 0));
        return;
      }

      if (event.target.closest('[data-gm-preview-close]')) {
        this.closePreview();
        return;
      }

      const previewScroll = event.target.closest('[data-gm-preview-scroll]');
      if (previewScroll) {
        this.scrollPreview(previewScroll.dataset.gmPreviewScroll);
        return;
      }

      const previewDialog = event.target.closest('[data-gm-preview-dialog]');
      if (previewDialog && event.target === previewDialog) {
        this.closePreview();
        return;
      }

      const scrollButton = event.target.closest('[data-gm-scroll]');
      if (scrollButton) {
        const carousel = this.querySelector('[data-gm-carousel]');
        const direction = scrollButton.dataset.gmScroll === 'next' ? 1 : -1;
        carousel?.scrollBy({ left: carousel.clientWidth * 0.72 * direction, behavior: 'smooth' });
        return;
      }
      if (event.target.closest('[data-gm-learn-more]')) return;
      if (this.readState().selectionMode === 'multiple') return;
      const card = event.target.closest('[data-gm-gift-card]');
      if (card && card.getAttribute('aria-disabled') !== 'true') {
        const group = card.closest('[data-gm-choice-group]');
        if (group) group.dataset.expanded = 'false';
        controller.requestSelection(card.dataset.variantId);
      }
    }

    handleKeydown(event) {
      if (this.readState().selectionMode === 'multiple') return;
      if (event.target.closest('[data-gm-learn-more]')) return;
      const previewDialog = this.querySelector('[data-gm-preview-dialog]');
      if (previewDialog?.open && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        this.scrollPreview(event.key === 'ArrowRight' ? 'next' : 'prev');
        return;
      }

      const card = event.target.closest('[data-gm-gift-card]');
      if (!card || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      if (card.getAttribute('aria-disabled') !== 'true') {
        const group = card.closest('[data-gm-choice-group]');
        if (group) group.dataset.expanded = 'false';
        controller.requestSelection(card.dataset.variantId);
      }
    }

    openPreview(index = 0) {
      const dialog = this.querySelector('[data-gm-preview-dialog]');
      if (!dialog) return;

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      const carousel = dialog.querySelector('[data-gm-preview-carousel]');
      nextMicrotask(() => {
        carousel?.scrollTo({
          left: carousel.clientWidth * Math.max(0, Number(index) || 0),
          behavior: 'auto'
        });
      });
    }

    closePreview() {
      const dialog = this.querySelector('[data-gm-preview-dialog]');
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    }

    scrollPreview(direction) {
      const carousel = this.querySelector('[data-gm-preview-carousel]');
      if (!carousel) return;
      carousel.scrollBy({
        left: carousel.clientWidth * (direction === 'next' ? 1 : -1),
        behavior: 'smooth'
      });
    }

    applySelection(variantId, pending) {
      this.querySelectorAll('[data-gm-gift-card]').forEach(card => {
        const isSelected = asString(card.dataset.variantId) === asString(variantId);
        card.classList.toggle('is-selected', isSelected);
        card.classList.toggle('is-pending', isSelected && pending);
        card.setAttribute('aria-pressed', String(isSelected));
        const statusRow = card.querySelector('.gm-card__status-row');
        const selectedLabel = statusRow?.querySelector('.gm-selected-label');
        if (!isSelected) {
          selectedLabel?.remove();
        } else if (statusRow && !selectedLabel) {
          const label = document.createElement('span');
          label.className = 'gm-selected-label';
          label.textContent = this.readState().selectedLabel || 'Selected';
          statusRow.appendChild(label);
        }
      });
    }

    applyQuantities(quantities, {allowance, pending, resetting = false, reselectionNotice = false}) {
      const state = this.readState();
      this.classList.toggle('is-resetting', resetting);
      const resetStatus = this.querySelector('[data-gm-gift-reset-status]');
      if (resetStatus) {
        resetStatus.hidden = !(resetting || reselectionNotice);
        resetStatus.textContent = (state.resetLabel || '').replaceAll('{count}', String(allowance));
      }
      const resetRetry = this.querySelector('[data-gm-gift-reset-retry]');
      if (resetRetry) {
        resetRetry.hidden = !(resetting && controller.quantityResetRetryRequired);
        resetRetry.disabled = controller.quantityWorkerQueued;
      }
      this.querySelectorAll('[data-gm-gift-selection-summary]').forEach(node => { node.hidden = resetting; });
      const available = new Set((state.selectableVariantIds || []).map(asString));
      const total = Object.values(quantities).reduce((sum, quantity) => sum + Number(quantity || 0), 0);
      this.querySelectorAll('[data-gm-gift-card]').forEach(card => {
        const variantId = asString(card.dataset.variantId);
        const quantity = Number(quantities[variantId]) || 0;
        card.classList.toggle('is-selected', quantity > 0);
        card.classList.toggle('is-pending', pending);
        const count = card.querySelector('[data-gm-gift-quantity]');
        if (count) count.textContent = asString(quantity);
        card.querySelectorAll('[data-gm-gift-adjust]').forEach(button => {
          const increase = Number(button.dataset.gmGiftAdjust) > 0;
          button.disabled = resetting || (increase ? !available.has(variantId) || total >= allowance : quantity <= 0);
          button.setAttribute('aria-disabled', asString(button.disabled));
        });
      });
      this.querySelectorAll('[data-gm-gift-selected-count]').forEach(node => { node.textContent = asString(total); });
      this.querySelectorAll('[data-gm-gift-remaining-count]').forEach(node => { node.textContent = asString(Math.max(0, allowance - total)); });
    }

    applyTiered(selections, {pending, retryRequired}) {
      const state = this.readState();
      if (state.unifiedChoices) {
        this.syncChoiceCategory();
        const ids = new Set(Object.values(selections)), limit = controller.quantityAllowance(state);
        const allowed = controller.tierChoices(state), count = ids.size;
        const counter = this.querySelector('[data-gm-choice-counter]');
        if (counter) counter.textContent = state.splitChoices ? controller.tierGroups(state).map(group => `${group.id === 'standard' ? 'Standard' : 'Premium'} ${group.variantIds.some(id => ids.has(id)) ? 1 : 0}/1`).join(' · ') : `${count} of ${limit} selected`;
        const groups = state.splitChoices ? controller.tierGroups(state) : [];
        this.querySelectorAll('[data-gm-gift-slot]').forEach(slot => {
          const group = groups.find(group => group.id === slot.dataset.gmGiftSlot);
          const selectedId = group?.variantIds.find(id => ids.has(id));
          const card = [...this.querySelectorAll('[data-gm-gift-card]')].find(card => asString(card.dataset.variantId) === selectedId);
          slot.classList.toggle('is-complete', Boolean(selectedId));
          const status = slot.querySelector('[data-gm-slot-status]');
          if (status) status.textContent = selectedId ? `✓ ${card?.dataset.giftName || 'Selected'}` : 'Please choose →';
        });
        const notice = this.querySelector('[data-gm-choice-notice]');
        if (notice) {
          const invalid = Number(state.invalidSelectedCount) > 0;
          if (invalid) controller.unifiedUnavailableNotice = true;
          notice.textContent = controller.unifiedReselectionContext === controller.tierContext(state) && count < limit ? `Your order changed. Please choose ${limit} FREE gift${limit === 1 ? '' : 's'} again.`
            : controller.unifiedUnavailableNotice && count < limit ? 'A previous gift is no longer available for this order. Please choose again.'
            : state.splitChoices ? 'Choose 1 standard gift + 1 premium gift. Tap a category below.'
            : limit > 1 ? `${limit} FREE gifts unlocked. Choose your favorites below.`
            : Number(state.activeTier) >= 2 ? 'Premium gifts unlocked. Keep your gift or choose an upgrade.'
            : 'A FREE gift is yours. Please choose below.';
          notice.classList.toggle('is-over-limit', count > limit);
          if (count >= limit && !invalid) controller.unifiedUnavailableNotice = false;
          if (count >= limit) controller.unifiedReselectionContext = null;
        }
        this.querySelectorAll('[data-gm-gift-card]').forEach(card => {
          const id = asString(card.dataset.variantId), selected = ids.has(id);
          const disabled = !allowed.has(id) || (!state.splitChoices && !selected && limit > 1 && count >= limit);
          card.classList.toggle('is-selected', selected);
          card.classList.toggle('is-pending', selected && pending);
          card.setAttribute('aria-pressed', String(selected));
          card.setAttribute('aria-disabled', String(disabled));
          if (card.tagName === 'BUTTON') card.disabled = disabled;
          const label = card.querySelector('[data-gm-tier-choice-label]');
          if (label) label.textContent = !allowed.has(id) ? (card.closest('.is-gift-locked') ? card.dataset.selectLabel || 'Buy 2 to unlock' : 'Sold out')
            : selected ? (limit > 1 && !state.splitChoices ? 'Selected · Remove' : 'Selected')
            : disabled ? 'Unselect a gift first' : groups.some(group => group.variantIds.includes(id) && group.variantIds.some(selectedId => ids.has(selectedId))) ? 'Replace gift' : 'Select gift';
        });
        const retry = this.querySelector('[data-gm-tier-retry]');
        if (retry) { retry.hidden = !retryRequired; retry.disabled = controller.tierWorkerQueued; }
        return;
      }
      const nextTier = this.querySelector('[data-gm-next-tier]');
      if (nextTier) {
        nextTier.textContent = state.nextTierMessage || '';
        nextTier.hidden = !state.nextTierMessage;
      }
      const choices = controller.tierChoices(state);
      let choiceName = '';
      const choiceVariantId = selections.premium || '';
      this.querySelectorAll('[data-gm-gift-card]').forEach(card => {
        const groupId = card.closest('[data-gm-choice-group]')?.dataset.gmChoiceGroup || 'premium';
        const id = asString(card.dataset.variantId), selected = id === selections[groupId];
        card.classList.toggle('is-selected', selected);
        card.classList.toggle('is-pending', selected && pending);
        card.setAttribute('aria-pressed', String(selected));
        card.setAttribute('aria-disabled', String(!choices.has(id)));
        if (card.tagName === 'BUTTON') card.disabled = !choices.has(id);
        const label = card.querySelector('[data-gm-tier-choice-label]');
        if (label) label.textContent = selected ? (card.dataset.selectedLabel || state.selectedLabel || 'Selected') : (card.dataset.selectLabel || 'Select gift');
        if (selected) choiceName = card.dataset.giftName || card.querySelector('.bfcm-gift__name')?.textContent?.trim() || '';
      });
      this.querySelectorAll('[data-gm-choice-group]').forEach(group => {
        const selected = selections[group.dataset.gmChoiceGroup];
        const card = [...group.querySelectorAll('[data-gm-gift-card]')].find(card => asString(card.dataset.variantId) === selected);
        const summary = group.querySelector('[data-gm-chosen]');
        const options = group.querySelector('[data-gm-choice-options]');
        summary.hidden = !card;
        options.hidden = Boolean(card && group.dataset.expanded !== 'true');
        group.querySelector('[data-gm-change-choice]').setAttribute('aria-expanded', String(!options.hidden));
        if (card) {
          group.querySelector('[data-gm-chosen-name]').textContent = card.dataset.giftName || '';
          const source = card.querySelector('img'), image = summary.querySelector('img');
          image.hidden = !source;
          if (source) {
            for (const attribute of ['src','srcset','alt']) {
              const value = source.getAttribute(attribute);
              if (value !== null) image.setAttribute(attribute, value);
              else image.removeAttribute(attribute);
            }
            image.setAttribute('sizes', '56px');
          }
        }
      });
      const status = this.querySelector('[data-gm-tier-choice-status]');
      if (status) {
        status.hidden = Number(state.choiceLimit) !== 1;
        status.textContent = choiceVariantId
          ? (state.tierChoiceSelectedTemplate || 'Selected: {name}').replaceAll('{name}', choiceName)
          : (state.tierChoicePrompt || 'Choose 1 premium gift');
      }
      const unavailable = this.querySelector('[data-gm-tier-fixed-unavailable]');
      if (unavailable) {
        unavailable.hidden = !controller.tierActive(state) || !(state.fixedGifts || []).some(gift => gift.available !== true);
        unavailable.textContent = state.fixedUnavailableLabel || 'A fixed gift is sold out.';
      }
      const retry = this.querySelector('[data-gm-tier-retry]');
      if (retry) {
        retry.hidden = !retryRequired;
        retry.disabled = controller.tierWorkerQueued;
      }
    }

    setBusy(value) {
      this.classList.toggle('is-busy', value);
      this.setAttribute('aria-busy', String(value));
      const status = this.querySelector('[data-bfcm-confirming]');
      if (status) status.hidden = !value;
    }

    showError(message) {
      const target = this.querySelector('[data-gm-error]');
      if (!target) return;
      target.textContent = message;
      target.hidden = !message;
    }
  }

  customElements.define(elementName, GptGiftMilestones);
})();


